import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Users, 
  Trophy, 
  TrendingUp, 
  TrendingDown, 
  Calendar, 
  MapPin,
  Flame,
  Target,
  Award
} from 'lucide-react';
import { usePartnershipHistory } from '@/hooks/usePartnershipHistory';
import { ClickableAvatar } from '@/components/ui/clickable-avatar';
import { format } from 'date-fns';

interface PartnershipHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  partnerId: string | null;
  partnerName?: string;
  profileUserId?: string;
}

const PartnershipHistoryDialog: React.FC<PartnershipHistoryDialogProps> = ({
  open,
  onOpenChange,
  partnerId,
  partnerName,
  profileUserId
}) => {
  const { stats, loading, error } = usePartnershipHistory(open ? partnerId : null, profileUserId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg h-[85vh] max-h-[85vh] flex flex-col overflow-hidden p-0">
        <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Partnership History
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="space-y-4 p-6">
            <div className="flex items-center gap-4">
              <Skeleton className="h-16 w-16 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
              <Skeleton className="h-20" />
            </div>
            <Skeleton className="h-40" />
          </div>
        ) : error ? (
          <div className="p-8 text-center text-muted-foreground">
            <p>Failed to load partnership history</p>
          </div>
        ) : stats ? (
          <ScrollArea className="flex-1 min-h-0 px-6 pb-6">
            <div className="space-y-6 pr-4">
              {/* Partner Header */}
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16 border-2 border-primary/20">
                  <AvatarImage src={stats.partnerAvatarUrl || ''} />
                  <AvatarFallback className="text-lg bg-primary/10 text-primary">
                    {stats.partnerName.split(' ').map(n => n[0]).join('').slice(0, 2)}
                  </AvatarFallback>
                </Avatar>
                <div>
                  <h3 className="text-lg font-semibold">{stats.partnerName}</h3>
                  <p className="text-sm text-muted-foreground">
                    {stats.totalMatches} matches together
                  </p>
                </div>
              </div>

              {/* Stats Grid */}
              <div className={`grid ${stats.draws > 0 ? 'grid-cols-4' : 'grid-cols-3'} gap-3`}>
                <div className="bg-primary/10 rounded-lg p-3 text-center">
                  <Trophy className="h-5 w-5 mx-auto mb-1 text-primary" />
                  <p className="text-2xl font-bold text-primary">{stats.wins}</p>
                  <p className="text-xs text-muted-foreground">Wins</p>
                </div>
                <div className="bg-destructive/10 rounded-lg p-3 text-center">
                  <TrendingDown className="h-5 w-5 mx-auto mb-1 text-destructive" />
                  <p className="text-2xl font-bold text-destructive">{stats.losses}</p>
                  <p className="text-xs text-muted-foreground">Losses</p>
                </div>
                {stats.draws > 0 && (
                  <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
                    <Target className="h-5 w-5 mx-auto mb-1 text-yellow-600" />
                    <p className="text-2xl font-bold text-yellow-600">{stats.draws}</p>
                    <p className="text-xs text-muted-foreground">Draws</p>
                  </div>
                )}
                <div className="bg-accent rounded-lg p-3 text-center">
                  <Target className="h-5 w-5 mx-auto mb-1 text-accent-foreground" />
                  <p className="text-2xl font-bold">{stats.winRate}%</p>
                  <p className="text-xs text-muted-foreground">Win Rate</p>
                </div>
              </div>

              {/* Additional Stats */}
              {stats.totalMatches > 0 && (
                <div className="grid grid-cols-2 gap-3">
                  {stats.currentStreak > 0 && (
                    <div className="border rounded-lg p-3 flex items-center gap-3">
                      <Flame className={`h-5 w-5 ${stats.streakType === 'win' ? 'text-orange-500' : 'text-blue-500'}`} />
                      <div>
                        <p className="text-sm font-medium">Current Streak</p>
                        <p className="text-xs text-muted-foreground">
                          {stats.currentStreak} {stats.streakType === 'win' ? 'wins' : 'losses'}
                        </p>
                      </div>
                    </div>
                  )}
                  {stats.bestStreak > 0 && (
                    <div className="border rounded-lg p-3 flex items-center gap-3">
                      <Award className="h-5 w-5 text-yellow-500" />
                      <div>
                        <p className="text-sm font-medium">Best Win Streak</p>
                        <p className="text-xs text-muted-foreground">{stats.bestStreak} consecutive wins</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <Separator />

              {/* Match History */}
              <div>
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Match History
                </h4>

                {stats.matches.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Users className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No matches played together yet</p>
                    <p className="text-sm">Play your first match as partners!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {stats.matches.map((match) => (
                      <div
                        key={match.id}
                        className={`border rounded-lg p-3 ${
                          match.result === 'win'
                            ? 'border-primary/30 bg-primary/5' 
                            : match.result === 'draw'
                            ? 'border-yellow-500/30 bg-yellow-500/5'
                            : 'border-destructive/30 bg-destructive/5'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Badge 
                              variant={match.result === 'win' ? 'default' : match.result === 'draw' ? 'outline' : 'destructive'}
                              className={`text-xs ${match.result === 'draw' ? 'border-yellow-500 text-yellow-600' : ''}`}
                            >
                              {match.result === 'win' ? 'WIN' : match.result === 'draw' ? 'DRAW' : 'LOSS'}
                            </Badge>
                            {match.date && (
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(match.date), 'MMM d, yyyy')}
                              </span>
                            )}
                          </div>
                          {match.location && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span className="truncate max-w-[100px]">{match.location}</span>
                            </div>
                          )}
                        </div>

                        {/* Score */}
                        {match.sets.length > 0 && (
                          <div className="flex gap-2 mb-2">
                            {match.sets.map((set, idx) => (
                              <span 
                                key={idx} 
                                className="text-sm font-mono bg-background/80 px-2 py-0.5 rounded"
                              >
                                {set.team1}-{set.team2}
                              </span>
                            ))}
                          </div>
                        )}

                        {/* Opponents */}
                        {match.opponents.length > 0 && (
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>vs</span>
                            <div className="flex items-center gap-1">
                              {match.opponents.map((opp, idx) => (
                                <React.Fragment key={opp.id}>
                                  <ClickableAvatar
                                    userId={opp.id}
                                    avatarUrl={opp.avatarUrl}
                                    fullName={opp.name}
                                    size="sm"
                                    className="h-5 w-5"
                                  />
                                  <span>{opp.name}</span>
                                  {idx < match.opponents.length - 1 && <span>&</span>}
                                </React.Fragment>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default PartnershipHistoryDialog;
